/**
 * Live reads for the workflow system (templates, runs, tasks). Writes go
 * through workflowsApi.js — Firestore rules make runs/tasks server-write-only.
 */
import {
  collection,
  collectionGroup,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { normAssigneeEmail } from '../utils/resolveFirmMilestoneAssignee';
import {
  ACTIVE_RUN_ROWS_COLLECTION,
  OPEN_TASK_ROWS_COLLECTION,
  markWorkflowListRow,
} from '../utils/workflowListRows';

const CLIENTS = 'clients';
const RUNS = 'workflowRuns';
const TASKS = 'workflowTasks';
const TEMPLATES = 'workflowTemplates';

function toIso(value) {
  if (value && typeof value.toDate === 'function') return value.toDate().toISOString();
  return value ?? null;
}

const TIMESTAMP_FIELDS = ['createdAt', 'lastModifiedAt', 'updatedAt', 'startedAt', 'completedAt', 'cancelledAt', 'skippedAt'];

function normaliseTimestamps(out) {
  TIMESTAMP_FIELDS.forEach((key) => {
    if (out[key] !== undefined) out[key] = toIso(out[key]);
  });
  return out;
}

export function mapWorkflowTaskDoc(d) {
  const data = d.data() || {};
  const out = normaliseTimestamps({ ...data, id: d.id });
  if (!out.clientId) out.clientId = d.ref?.parent?.parent?.id || null;
  if (!Array.isArray(out.fileNoteIds)) out.fileNoteIds = out.fileNoteIds ? [out.fileNoteIds] : [];
  if (!Array.isArray(out.checklist)) out.checklist = [];
  if (!Array.isArray(out.predecessorTaskIds)) out.predecessorTaskIds = [];
  if (!Array.isArray(out.waitingOnTaskIds)) out.waitingOnTaskIds = [];
  // Tasks written before multi-envelope support only carry the scalar link.
  if (!Array.isArray(out.envelopeIds)) out.envelopeIds = out.envelopeId ? [out.envelopeId] : [];
  if (!Array.isArray(out.taskNotes)) {
    out.taskNotes = [];
  } else {
    out.taskNotes = out.taskNotes.map((note) => ({ ...note, createdAt: toIso(note?.createdAt) }));
  }
  out.isWorkflowTask = true;
  return out;
}

export function mapWorkflowRunDoc(d) {
  const data = d.data() || {};
  const out = normaliseTimestamps({ ...data, id: d.id });
  if (!out.clientId) out.clientId = d.ref?.parent?.parent?.id || null;
  return out;
}

export function mapWorkflowTemplateDoc(d) {
  const data = d.data() || {};
  const out = normaliseTimestamps({ ...data, id: d.id });
  if (!Array.isArray(out.tasks)) out.tasks = [];
  if (!Array.isArray(out.links)) out.links = [];
  return out;
}

function subscribe(q, mapper, callback, label) {
  return onSnapshot(
    q,
    (snap) => callback(snap.docs.map(mapper)),
    (error) => {
      console.error(`Error subscribing to ${label}:`, error);
      callback([]);
    },
  );
}

/**
 * onSnapshot that skips cache-only snapshots until the server has answered
 * once. Other listeners (e.g. the Assigned inbox) leave some of the docs in the
 * memory cache, so the first snapshot would otherwise be a partial list.
 */
function onServerSnapshot(q, next, onError) {
  let serverSeen = false;
  return onSnapshot(
    q,
    { includeMetadataChanges: true },
    (snap) => {
      if (!serverSeen) {
        if (snap.metadata.fromCache) return;
        serverSeen = true;
      } else if (!snap.docChanges().length) {
        return; // metadata-only change: nothing to re-render
      }
      next(snap);
    },
    onError,
  );
}

/** Like subscribe(), but never delivers a partial list from the cache (the workflow map would fill in stages). */
function subscribeServerFirst(q, mapper, callback, label) {
  return onServerSnapshot(
    q,
    (snap) => callback(snap.docs.map(mapper)),
    (error) => {
      console.error(`Error subscribing to ${label}:`, error);
      callback([]);
    },
  );
}

/** Every non-archived template (filter by firm in the caller). */
export function subscribeToWorkflowTemplates(callback) {
  const q = query(collection(db, TEMPLATES), where('archived', '==', false));
  return subscribe(q, mapWorkflowTemplateDoc, callback, 'workflow templates');
}

export function subscribeToClientWorkflowRuns(clientId, callback) {
  if (!clientId) {
    callback([]);
    return () => {};
  }
  const q = query(collection(db, CLIENTS, clientId, RUNS), orderBy('startedAt', 'desc'));
  return subscribeServerFirst(q, mapWorkflowRunDoc, callback, 'workflow runs');
}

export function subscribeToClientWorkflowTasks(clientId, callback) {
  if (!clientId) {
    callback([]);
    return () => {};
  }
  const q = query(collection(db, CLIENTS, clientId, TASKS), orderBy('order', 'asc'));
  return subscribeServerFirst(q, mapWorkflowTaskDoc, callback, 'workflow tasks');
}

export function subscribeToWorkflowRun(clientId, runId, callback) {
  if (!clientId || !runId) {
    callback(null);
    return () => {};
  }
  return onSnapshot(
    doc(db, CLIENTS, clientId, RUNS, runId),
    (snap) => callback(snap.exists() ? mapWorkflowRunDoc(snap) : null),
    (error) => {
      console.error('Error subscribing to workflow run:', error);
      callback(null);
    },
  );
}

/** Tasks of one run (equality filter only — no composite index; sorted by order here). */
export function subscribeToWorkflowRunTasks(clientId, runId, callback) {
  if (!clientId || !runId) {
    callback([]);
    return () => {};
  }
  const q = query(collection(db, CLIENTS, clientId, TASKS), where('runId', '==', runId));
  return subscribeServerFirst(q, mapWorkflowTaskDoc, (tasks) => {
    callback([...tasks].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
  }, 'workflow run tasks');
}

/** Tasks of one run, read once (the live subscription can serve a partial list first). */
export async function fetchWorkflowRunTasks(clientId, runId) {
  if (!clientId || !runId) return [];
  const snap = await getDocs(query(collection(db, CLIENTS, clientId, TASKS), where('runId', '==', runId)));
  return snap.docs.map(mapWorkflowTaskDoc).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

export function subscribeToWorkflowTask(clientId, taskId, callback) {
  if (!clientId || !taskId) {
    callback(null);
    return () => {};
  }
  return onSnapshot(
    doc(db, CLIENTS, clientId, TASKS, taskId),
    (snap) => callback(snap.exists() ? mapWorkflowTaskDoc(snap) : null),
    (error) => {
      console.error('Error subscribing to workflow task:', error);
      callback(null);
    },
  );
}

function compareDue(a, b) {
  const da = a.dueDate || '';
  const dbb = b.dueDate || '';
  if (da && dbb && da !== dbb) return da < dbb ? -1 : 1;
  if (da && !dbb) return -1;
  if (!da && dbb) return 1;
  return String(a.title || '').localeCompare(String(b.title || ''));
}

/**
 * Open workflow tasks assigned to the user, across every client. Needs the
 * collection-group index on (status, assignedTo) — see firestore.indexes.json.
 */
export function subscribeToMyAssignedWorkflowTasks(userEmail, callback) {
  const email = normAssigneeEmail(userEmail);
  if (!email) {
    callback([]);
    return () => {};
  }
  const q = query(
    collectionGroup(db, TASKS),
    where('status', '==', 'open'),
    where('assignedTo', '==', email),
  );
  return subscribe(q, mapWorkflowTaskDoc, (tasks) => callback([...tasks].sort(compareDue)), 'my workflow tasks');
}

const FIRESTORE_IN_LIMIT = 30;

function scopedCollectionGroupQuery(name, statusValue, firmIds) {
  const ids = Array.isArray(firmIds) ? firmIds.filter(Boolean) : [];
  if (ids.length === 1) {
    return query(collectionGroup(db, name), where('advisoryTeamId', '==', ids[0]), where('status', '==', statusValue));
  }
  if (ids.length > 1 && ids.length <= FIRESTORE_IN_LIMIT) {
    return query(collectionGroup(db, name), where('advisoryTeamId', 'in', ids), where('status', '==', statusValue));
  }
  return query(collectionGroup(db, name), where('status', '==', statusValue));
}

// The slim rows (src/utils/workflowListRows.js) carry the firm but no status:
// a row exists only while its task is open / its run is active.
function scopedRowsQuery(collectionName, firmIds) {
  const ids = Array.isArray(firmIds) ? firmIds.filter(Boolean) : [];
  if (ids.length === 1) return query(collection(db, collectionName), where('advisoryTeamId', '==', ids[0]));
  if (ids.length > 1 && ids.length <= FIRESTORE_IN_LIMIT) {
    return query(collection(db, collectionName), where('advisoryTeamId', 'in', ids));
  }
  return collection(db, collectionName);
}

/**
 * Listen to slim rows, or to the full docs while there are none to read (the
 * server mirror has not written them, or their rule is not deployed yet).
 * `onDocs(items)` gets mapped items, never a partial list from the cache (the
 * portals filter on them and mark them loaded); `onError(error)` a final error.
 */
function subscribeRowsOrDocs({ rowsQuery, mapRow, docsQuery, mapDoc, onDocs, onError, label }) {
  let unsubscribe = null;
  let rowsSeen = false;
  const listenToDocs = (reason) => {
    console.info(`[${label}] reading full docs (${reason})`);
    unsubscribe = onServerSnapshot(docsQuery, (snap) => onDocs(snap.docs.map(mapDoc)), onError);
  };
  unsubscribe = onServerSnapshot(
    rowsQuery,
    (snap) => {
      if (!rowsSeen && snap.empty) {
        unsubscribe();
        listenToDocs('no rows');
        return;
      }
      rowsSeen = true;
      onDocs(snap.docs.map(mapRow));
    },
    (error) => {
      if (!rowsSeen && error?.code === 'permission-denied' && auth.currentUser) {
        listenToDocs('rows not readable');
        return;
      }
      onError(error);
    },
  );
  return () => {
    if (unsubscribe) unsubscribe();
  };
}

function mapOpenTaskRow(d) {
  const data = d.data() || {};
  return markWorkflowListRow(mapWorkflowTaskDoc({ id: data.taskId || d.id, data: () => data, ref: null }));
}

function mapActiveRunRow(d) {
  const data = d.data() || {};
  return markWorkflowListRow(mapWorkflowRunDoc({ id: data.runId || d.id, data: () => data, ref: null }));
}

/**
 * Every open workflow task, optionally limited to some firms (client-list
 * filter + Open Task columns), as slim rows: no notes, checklist or completion
 * detail — open the task itself for those. The full-doc fallback is served by
 * the `workflowTasks.status` field override (unscoped) or the
 * (advisoryTeamId, status) CG index.
 */
export function subscribeToOpenWorkflowTasks({ firmIds = null } = {}, callback) {
  return subscribeRowsOrDocs({
    rowsQuery: scopedRowsQuery(OPEN_TASK_ROWS_COLLECTION, firmIds),
    mapRow: mapOpenTaskRow,
    docsQuery: scopedCollectionGroupQuery(TASKS, 'open', firmIds),
    mapDoc: mapWorkflowTaskDoc,
    onDocs: callback,
    onError: (error) => {
      console.error('Error subscribing to open workflow tasks:', error);
      callback([]);
    },
    label: 'openWorkflowTasks',
  });
}

/**
 * Every active workflow run, optionally limited to some firms, as slim rows
 * (the template copy keeps only what the Annual Review check reads). Without
 * the rows, the collection-group read rule on workflowRuns (firestore.rules)
 * serves the full docs; if that read is refused too the callback receives
 * `([], error)`.
 */
export function subscribeToActiveWorkflowRuns({ firmIds = null } = {}, callback) {
  return subscribeRowsOrDocs({
    rowsQuery: scopedRowsQuery(ACTIVE_RUN_ROWS_COLLECTION, firmIds),
    mapRow: mapActiveRunRow,
    docsQuery: scopedCollectionGroupQuery(RUNS, 'active', firmIds),
    mapDoc: mapWorkflowRunDoc,
    onDocs: (runs) => callback(runs, null),
    onError: (error) => {
      console.error('Error subscribing to active workflow runs:', error);
      callback([], error);
    },
    label: 'activeWorkflowRuns',
  });
}
